import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import ForgotPassword from './partials/ForgotPassword';
import AppTheme from '@templates/shared-theme/AppTheme';
import ColorModeSelect from '@templates/shared-theme/ColorModeSelect';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { loginAction } from '@redux/authReducer'
import { Helmet } from 'react-helmet'
import { useDispatch } from 'react-redux'
import { useGlobalContext } from '@providers/globalProvider'
import { useNavigate } from 'react-router-dom'
import { login } from '@services/auth';
import IndexLogo from '@layouts/partials/header/partials/indexLogo'

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
    height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
            backgroundImage:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

const validationSchema = Yup.object({
    email: Yup.string().email('Email không hợp lệ').required('Vui lòng nhập email'),
    password: Yup.string().min(6, 'Mật khẩu ít nhất 6 ký tự').required('Vui lòng nhập mật khẩu'),
});

export default function SignIn(props) {
    const [open, setOpen] = React.useState(false);
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { showNotification, showLoading, hideLoading } = useGlobalContext()

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleSubmit = (values) => {
        onLogin(values)
    };


    const onLogin = ({ email, password }) => {
        showLoading()
        login({ email, password })
            .then(res => {
                showNotification(res.data.message, 'success')
                hideLoading()
                dispatch(loginAction(res.data.data))
                navigate('/')
            })
            .catch(err => {
                showNotification('Thông tin đăng nhập chưa chính xác hoặc bạn không có quyền truy cập trang này', 'error')
                hideLoading()
            })
    }

    return (
        <AppTheme {...props}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Đăng nhập</title>
                <meta name="description" content={'Đăng nhập'} />
            </Helmet>
            <CssBaseline enableColorScheme />
            <SignInContainer direction="column" justifyContent="space-between">
                <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
                <Card variant="outlined">
                    <IndexLogo />
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                    >
                        Đăng nhập
                    </Typography>

                    <Formik
                        initialValues={{ email: '', password: '', remember: false }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, touched, handleChange, handleSubmit }) => (
                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                noValidate
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    gap: 2,
                                }}
                            >
                                <FormControl>
                                    <FormLabel htmlFor="email">Email</FormLabel>
                                    <TextField
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="your@email.com"
                                        autoComplete="email"
                                        autoFocus
                                        fullWidth
                                        required
                                        variant="outlined"
                                        value={values.email}
                                        onChange={handleChange}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel htmlFor="password">Mật khẩu</FormLabel>
                                    <TextField
                                        id="password"
                                        name="password"
                                        type="password"
                                        placeholder="••••••"
                                        autoComplete="current-password"
                                        fullWidth
                                        required
                                        variant="outlined"
                                        value={values.password}
                                        onChange={handleChange}
                                        error={touched.password && Boolean(errors.password)}
                                        helperText={touched.password && errors.password}
                                    />
                                </FormControl>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="remember"
                                            color="primary"
                                            checked={values.remember}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Ghi nhớ mật khẩu"
                                />

                                <ForgotPassword open={open} handleClose={handleClose} />

                                <Button type="submit" fullWidth variant="contained">
                                    Đăng nhập
                                </Button>

                                <Link
                                    component="button"
                                    type="button"
                                    onClick={handleClickOpen}
                                    variant="body2"
                                    sx={{ alignSelf: 'center' }}
                                >
                                    Bạn quên mật khẩu?
                                </Link>
                            </Box>
                        )}
                    </Formik>
                </Card>
            </SignInContainer>
        </AppTheme>
    );
}
